<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="getShowAllCards"
            :is-loading="getIsLoadingShowAll"
            @item-selected="itemSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>

<script lang="ts" setup>
    import { type ComputedRef, computed} from "vue";
    import type { OnDemandCardData } from "@/components/Cards/types";
    import { Strings } from "@/locales/types/enums";
    import { linkItems, ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import { useRoute, useRouter } from 'vue-router'
    import { useI18n } from "vue-i18n";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useVodPageStore } from "@/stores/VodPage";
    import { storeToRefs } from 'pinia';
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useApplicationStore } from "@/stores/Application";

    const VodPageStore = useVodPageStore();
    const {
        loadShowAllNextPage,
    } = VodPageStore;
    const {
        getShowAllCards,
        getIsLoadingShowAll,
    } = storeToRefs(VodPageStore);

    const { 
        setViewedDetailItems,
    } = useDetailPageStore();

    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-show-all-vod'],
    });

    const { $navigator } = useNuxtApp();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();

    const routeQueryType: string = route.query.type as string;

    const pageTitle: ComputedRef<string> = computed(() => {
        switch (routeQueryType) {
            case ShowAllContentType.ContinueWatching:
                return t(Strings.HOME_FIZZ_CONTINUE_WATCHING).toString();
            case ShowAllContentType.MostPopularVod:
                return t(Strings.VOD_MOST_POPULAR).toString();
            case ShowAllContentType.NewMovies:
                return t(Strings.VOD_NEW_MOVIES).toString();
            case ShowAllContentType.NewSeries:
                return t(Strings.VOD_NEW_SHOWS).toString();
            case ShowAllContentType.RecommendedMovies:
                return t(Strings.VOD_RECOMMENDED_MOVIES).toString();
            case ShowAllContentType.RecommendedSeries:
                return t(Strings.VOD_RECOMMENDED_SHOWS).toString();
            case ShowAllContentType.WatchAgain:
                return t(Strings.HOME_WATCH_AGAIN).toString();
            default:
                Logger.captureError(`ShowAll: Unknown type page ${routeQueryType}` as unknown as Error);
                return "";
        }
    });

    const fetchMoreItems = async (): Promise<void> => {
        await loadShowAllNextPage();
    };
    const itemSelected = (event: IEventSelected): void => {
        try {
            const item = event.item as OnDemandCardData;

            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(event.item));
            }
            setViewedDetailItems(playItem);
            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${item.getId()}` :
                `${linkItems.detailsVodMovies.routerPath}/${item.getId()}`;
            void router.push(path);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
</script>
